import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import SummaryApi from '../common/index';
import * as XLSX from 'xlsx'; // Requirement for Excel Export
import {
  User,
  Phone,
  Building,
  MapPin,
  Home,
  Eye,
  EyeOff,
  Calendar,
  CheckCircle,
  Users,
  DollarSign,
  ChevronLeft,
  ChevronRight,
  Search,
  Download
} from 'lucide-react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { FaRupeeSign } from 'react-icons/fa';

ChartJS.register(ArcElement, Tooltip, Legend);

const ConsultantDashboard = () => {
  const { encodedId } = useParams();
  const [consultant, setConsultant] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [todayRegistrations, setTodayRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  // New Search State
  const [searchTerm, setSearchTerm] = useState('');

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 15;

  useEffect(() => {
    if (consultant?.consultancy_name) {
      const event = new CustomEvent('updateConsultancyHeader', { 
        detail: consultant.consultancy_name 
      });
      window.dispatchEvent(event);
    }
    return () => {
      window.dispatchEvent(new CustomEvent('updateConsultancyHeader', { detail: '' }));
    };
  }, [consultant]);

  useEffect(() => {
    if (!encodedId) {
      setError('No consultant identifier provided');
      setLoading(false);
      return;
    }

    let decodedId;
    try {
      decodedId = atob(encodedId);
    } catch {
      setError('Invalid link format');
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        // 1. Fetch Profile
        const profileResponse = await fetch(SummaryApi.GetconsultantByid.url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ admin_id: decodedId }),
        });

        if (!profileResponse.ok) throw new Error(`Profile HTTP ${profileResponse.status}`);
        const profileResult = await profileResponse.json();

        if (profileResult.status === true && profileResult.data?.length > 0) {
          const raw = profileResult.data[0];
          setConsultant({
            ...raw,
            mobile: raw.mobile_number1 || raw.mobile_number2 || 'N/A',
            secondMobile: raw.mobile_number2 || null,
            address: raw.address?.trim() || 'N/A',
            city: raw.city?.trim() || 'N/A',
            registered_on: raw.created_date && raw.created_date !== '0000-00-00 00:00:00.000000'
              ? new Date(raw.created_date).toLocaleString('en-IN') : 'N/A',
            is_active: raw.is_active === '1' ? 'Active' : 'Inactive',
            fullNameDisplay: raw.full_name || raw.user_name || 'N/A',
            password: raw.password || 'N/A',
          });

          // 2. Fetch Dashboard Summary
          const dashboardResponse = await fetch(SummaryApi.getDashboardSummary.url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ admin_id: decodedId }),
          });
          if (dashboardResponse.ok) {
            const dashboardResult = await dashboardResponse.json();
            if (dashboardResult.status === true) setDashboardData(dashboardResult.data);
          }

          // 3. Fetch Student Data
          const studentResponse = await fetch(SummaryApi.getTotalregistrationsbyconsultantid.url, {
            method: SummaryApi.getTotalregistrationsbyconsultantid.method,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ admin_id: decodedId }),
          });

          if (studentResponse.ok) {
            const studentResult = await studentResponse.json();
            if (studentResult.status === true && Array.isArray(studentResult.data)) {
              setTodayRegistrations(studentResult.data);
            }
          }
        } else {
          setError(profileResult.message || 'No consultant data found');
        }
      } catch (err) {
        console.error(err);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [encodedId]);

  // Helper to force N/A on empty values
  const formatTableCell = (value) => {
    if (value === null || value === undefined || String(value).trim() === '' || value === '0000-00-00' || value === '0000-00-00 00:00:00.000000') {
      return 'N/A';
    }
    return value;
  };

  // Filter Logic for Search
  const filteredRecords = todayRegistrations.filter(student => 
    student.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    
    student.student_enrollment_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.mobile_number?.includes(searchTerm)
  );

  // Pagination Logic
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);

  // Export to Excel Function
  const exportToExcel = () => {
    const dataToExport = todayRegistrations.map((student, index) => ({
      "S.No": index + 1,
      "Student Name": student.student_name,
      "Enrollment No": student.student_enrollment_number,
      
      "Receipt No": student.receipt_number,
      "Receipt Date": student.billing_created_date,
      "Amount": student.amount,
      "Branch": student.student_branch,
      "Year": student.student_year,
      "Payment Mode": student.payment_mode,
      "Mobile": student.mobile_number,
      "Registered On": student.student_created_date,
      "Status": student.student_billing_id ? "Paid" : "Pending"
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Students");
    XLSX.writeFile(workbook, `Student_Report_${new Date().toLocaleDateString()}.xlsx`);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-50 text-blue-700 animate-pulse text-xl">Loading...</div>;
  if (error) return <div className="min-h-screen flex items-center justify-center bg-gray-50 text-red-600 text-xl font-medium px-4 text-center">{error}</div>;

  const summary = dashboardData?.summary || {};
  const pageTitle = consultant?.consultancy_name ? `${consultant.consultancy_name} Dashboard` : 'Consultant Dashboard';

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pb-10">

      {/* Stats Widgets */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 px-5 md:px-8 mb-10 pt-8">
        <StatWidget icon={<Users size={28} />} label="Total Students" value={formatTableCell(summary.total_students)} gradient="from-blue-500 to-blue-700" />
        <StatWidget icon={<Calendar size={28} />} label="Registered Today" value={formatTableCell(summary.today_students)} gradient="from-cyan-500 to-blue-600" />
        <StatWidget icon={<CheckCircle size={28} />} label="Billed Students" value={formatTableCell(summary.total_billing_students)} gradient="from-indigo-500 to-blue-600" />
        <StatWidget icon={<FaRupeeSign size={28} />} label="Total Fee Collected" value={`₹${formatTableCell(summary.total_fee_collected)}`} gradient="from-blue-600 to-indigo-600" />
      </div>

      {/* Student Table */}
      <div className="bg-white rounded-2xl shadow-md border border-blue-100/60 overflow-hidden mx-5 md:mx-8 mb-6">
        <div className="bg-gradient-to-r from-blue-50 to-blue-100/40 px-6 py-5 border-b border-blue-100 flex flex-col md:flex-row justify-between items-center gap-4">
          <h2 className="text-xl font-semibold text-gray-900">Registration & Billing Details</h2>
          
          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
            {/* Search Bar */}
            <div className="relative flex-1 md:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input 
                type="text"
                placeholder="Search Name or Mobile or Enrollment No..."
                className="w-full pl-10 pr-4 py-2 rounded-xl border border-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              />
            </div>

            {/* Export Button */}
            <button 
              onClick={exportToExcel}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-xl text-sm font-medium transition-all shadow-sm"
            >
              <Download size={18} /> Export Excel
            </button>
            
            <span className="text-sm font-medium text-blue-800 bg-blue-100 px-3 py-1 rounded-full">
              Records: {filteredRecords.length}
            </span>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-blue-50">
            <thead className="bg-blue-50/70">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-blue-800 uppercase">S.No</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-blue-800 uppercase">Student Name</th>
                     <th className="px-6 py-4 text-left text-xs font-bold text-blue-800 uppercase">Student Mobile Number</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-blue-800 uppercase">Enrollment No.</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-blue-800 uppercase">Receipt No.</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-blue-800 uppercase">Receipt Date</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-blue-800 uppercase">Amount</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-blue-800 uppercase">Branch</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-blue-800 uppercase">Year</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-blue-800 uppercase">Payment Mode</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-blue-800 uppercase">Registered On</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-blue-800 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-blue-50">
              {currentRecords.length > 0 ? (
                currentRecords.map((student, index) => (
                  <tr key={index} className="hover:bg-blue-50/40 transition-colors">
                    <td className="px-6 py-4 text-sm text-gray-700 font-medium">{indexOfFirstRecord + index + 1}</td>
                    <td className="px-6 py-4 text-sm font-semibold text-gray-900">{formatTableCell(student.student_name)}</td>
                               <td className="px-6 py-4 text-sm font-semibold text-gray-900">{formatTableCell(student.mobile_number)}</td>
                    <td className="px-6 py-4 text-sm text-gray-700 font-mono text-xs">{formatTableCell(student.student_enrollment_number)}</td>
                    <td className="px-6 py-4 text-sm text-gray-600 font-mono text-xs">{formatTableCell(student.receipt_number)}</td>
                    <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">
                      {student.billing_created_date && student.billing_created_date !== '0000-00-00 00:00:00.000000' 
                        ? new Date(student.billing_created_date).toLocaleDateString('en-IN') 
                        : 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm font-bold text-blue-700">
                        {student.amount ? `₹${student.amount}` : 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">{formatTableCell(student.student_branch)}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{formatTableCell(student.student_year)}</td>
                    <td className="px-6 py-4 text-sm text-gray-600 text-xs">{formatTableCell(student.payment_mode)}</td>
                    <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">
                      {student.student_created_date && student.student_created_date !== '0000-00-00 00:00:00.000000' 
                        ? new Date(student.student_created_date).toLocaleDateString('en-IN') 
                        : 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {student.student_billing_id ? (
                        <span className="text-[11px] text-green-700 bg-green-100 px-2 py-0.5 rounded-md font-bold border border-green-200 uppercase tracking-wider">Paid</span>
                      ) : (
                        <span className="text-[11px] text-amber-700 bg-amber-100 px-2 py-0.5 rounded-md font-bold border border-amber-200 uppercase tracking-wider">Pending</span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={12} className="px-6 py-8 text-center text-gray-500 italic">No records matching your search</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="px-6 py-4 bg-gray-50 border-t border-blue-100 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Showing <span className="font-semibold">{indexOfFirstRecord + 1}</span> to <span className="font-semibold">{Math.min(indexOfLastRecord, filteredRecords.length)}</span> of <span className="font-semibold">{filteredRecords.length}</span> entries
            </p>
            <div className="flex gap-2">
              <button 
                onClick={() => { setCurrentPage(prev => Math.max(prev - 1, 1)); window.scrollTo(0, 400); }}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-blue-200 bg-white disabled:opacity-50 hover:bg-blue-50 text-blue-600"
              >
                <ChevronLeft size={20} />
              </button>
              <button 
                onClick={() => { setCurrentPage(prev => Math.min(prev + 1, totalPages)); window.scrollTo(0, 400); }}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg border border-blue-200 bg-white disabled:opacity-50 hover:bg-blue-50 text-blue-600"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Consultant Details Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 px-5 md:px-8">
        <div className="bg-white rounded-2xl shadow-md border border-blue-100/60 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-50 to-blue-100/40 px-6 py-5 border-b border-blue-100">
            <div className="flex items-center gap-4">
              <div className="bg-white p-4 rounded-full shadow-sm"><User size={36} className="text-blue-600" /></div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{formatTableCell(consultant.fullNameDisplay)}</h2>
                <p className="text-blue-700 font-medium mt-1">{formatTableCell(consultant.consultancy_name)}</p>
              </div>
            </div>
          </div>
          <div className="p-6 space-y-5">
            <InfoRow icon={<Phone />} label="Mobile" value={formatTableCell(consultant.mobile)} />
            <InfoRow icon={<Building />} label="Consultancy" value={formatTableCell(consultant.consultancy_name)} />
            <InfoRow icon={<Home />} label="Address" value={formatTableCell(consultant.address)} isMultiLine />
            <InfoRow icon={<MapPin />} label="City" value={formatTableCell(consultant.city)} />
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-md border border-blue-100/60 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-50 to-blue-100/40 px-6 py-5 border-b border-blue-100">
            <h2 className="text-xl font-semibold text-gray-900">Account & Membership</h2>
          </div>
          <div className="p-6 space-y-4">
            <DetailRow label="Member Type" value={formatTableCell(consultant.member_type)} />
            <DetailRow label="Status" value={consultant.is_active} badge={consultant.is_active === 'Active' ? 'green' : 'red'} />
            <DetailRow label="Username" value={formatTableCell(consultant.user_name)} />
            <div className="flex justify-between items-center py-3 border-b border-blue-50">
              <span className="text-gray-700 font-medium">Password</span>
              <div className="flex items-center gap-3">
                <span className="font-mono font-semibold text-gray-900">{showPassword ? formatTableCell(consultant.password) : '••••••••••'}</span>
                <button onClick={() => setShowPassword(!showPassword)} className="text-blue-600 hover:text-blue-800">
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const StatWidget = ({ icon, label, value, gradient }) => (
  <div className={`bg-gradient-to-br ${gradient} rounded-2xl shadow-lg p-6 text-white`}>
    <div className="flex items-center gap-5">
      <div className="bg-white/20 backdrop-blur-sm p-4 rounded-full">{icon}</div>
      <div>
        <p className="text-sm opacity-90 font-medium">{label}</p>
        <p className="text-3xl font-bold mt-1">{value}</p>
      </div>
    </div>
  </div>
);

const InfoRow = ({ icon, label, value, isMultiLine = false }) => (
  <div className="flex items-start gap-4">
    <div className="text-blue-600 mt-1">{icon}</div>
    <div className="flex-1">
      <p className="text-sm text-gray-600 font-medium">{label}</p>
      <p className={`font-semibold text-gray-900 mt-0.5 ${isMultiLine ? 'whitespace-pre-line' : ''}`}>{value}</p>
    </div>
  </div>
);

const DetailRow = ({ label, value, badge }) => (
  <div className="flex justify-between items-center py-3 border-b border-blue-50 last:border-0">
    <span className="text-gray-700 font-medium">{label}</span>
    {badge ? (
      <span className={`px-4 py-1.5 rounded-full text-sm font-semibold border ${badge === 'green' ? 'bg-green-100 text-green-800 border-green-200' : 'bg-red-100 text-red-800 border-red-200'}`}>{value}</span>
    ) : (
      <span className="font-semibold text-gray-900">{value}</span>
    )}
  </div>
);

export default ConsultantDashboard;